import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import axios from 'axios';

// Critical pages - eager loaded (above the fold, most visited)
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';

// Non-critical pages - lazy loaded (reduces initial bundle size)
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const ExpressCheckoutPage = lazy(() => import('./pages/ExpressCheckoutPage'));
const CryptoPaymentPage = lazy(() => import('./pages/CryptoPaymentPage'));
const WisePaymentPage = lazy(() => import('./pages/WisePaymentPage'));
const RevolutPaymentPage = lazy(() => import('./pages/RevolutPaymentPage'));
const EFTPaymentPage = lazy(() => import('./pages/EFTPaymentPage'));
const PayPalPaymentPage = lazy(() => import('./pages/PayPalPaymentPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BuyerOrdersPage = lazy(() => import('./pages/BuyerOrdersPage'));
const BuyerOrderDetailPage = lazy(() => import('./pages/BuyerOrderDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
import AuthModal from './components/AuthModal';
import { Toaster } from './components/ui/sonner';
import WhatsAppButton from './components/WhatsAppButton';
import BackToTop from './components/BackToTop';
import CookieConsent from './components/CookieConsent';
import ExitIntentPopup from './components/ExitIntentPopup';
import PromoBanner from './components/PromoBanner';
import CartDrawer from './components/CartDrawer';
import { trackPageView } from './utils/analytics';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Analytics Tracker Component - tracks page views
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const sendBackendPageview = async () => {
      try {
        await axios.post(`${API}/track/pageview`, {
          path: location.pathname + location.search
        }, {
          withCredentials: false,
          timeout: 5000
        });
      } catch (error) {
        console.debug('Analytics tracking skipped');
      }
    };

    trackPageView(location.pathname + location.search, document.title);

    const timeoutId = setTimeout(sendBackendPageview, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);
  
  return null; // This component doesn't render anything
};

// 404 Page - SEO friendly with search
const NotFoundPage = () => {
  const [query, setQuery] = React.useState('');
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Page Not Found | North Cyprus Philately</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="The page you are looking for could not be found. Browse our stamp collection or search for what you need." />
      </Helmet>
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="text-center max-w-md">
          <div className="text-8xl mb-4">📮</div>
          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
          <h2 className="text-2xl font-bold mb-4">Page Not Found</h2>
          <p className="text-gray-600 mb-6">The stamp you're looking for might have been sold or moved to a different collection.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (query.trim()) window.location.href = `/shop?search=${encodeURIComponent(query.trim())}`; }} className="flex gap-2 mb-6">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for stamps..."
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C8A951] focus:border-transparent"
            />
            <button type="submit" className="btn-primary">Search</button>
          </form>
          <div className="flex gap-4 justify-center">
            <a href="/" className="btn-primary">Go Home</a>
            <a href="/shop" className="btn-secondary">Browse Shop</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  // Only block first paint behind the auth check when there is actually a
  // session to verify. Anonymous visitors — including every search-engine
  // crawler — render the full app immediately instead of a "Loading..." screen.
  const [loading, setLoading] = useState(() => {
    try {
      return Boolean(
        localStorage.getItem('session_token') ||
        document.cookie.includes('session_token') ||
        window.location.hash.includes('session_id=')
      );
    } catch {
      return true;
    }
  });
  const [cart, setCart] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const openCartDrawer = () => setCartDrawerOpen(true);
  const closeCartDrawer = () => setCartDrawerOpen(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('signin');
  const openAuthModal = (tab = 'signin') => { setAuthModalTab(tab); setAuthModalOpen(true); };
  const closeAuthModal = () => setAuthModalOpen(false);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('siteLanguage') || 'en';
  });
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    return localStorage.getItem('displayCurrency') || 'USD';
  });

  // Fallback rates used only until the live rates load (or if the fetch fails)
  const [currencyRates, setCurrencyRates] = useState({ USD: 1, EUR: 0.92, GBP: 0.79 });
  const currencySymbols = { USD: '$', EUR: '€', GBP: '£' };

  useEffect(() => {
    axios.get(`${API}/fx-rates`)
      .then(res => {
        if (res.data?.rates) setCurrencyRates(prev => ({ ...prev, ...res.data.rates }));
      })
      .catch(() => {
        // Keep fallback rates — display stays approximate but never breaks
      });
  }, []);

  const convertPrice = (priceUSD) => {
    const rate = currencyRates[displayCurrency] || 1;
    return (priceUSD * rate).toFixed(2);
  };
  const handleSetDisplayCurrency = (c) => {
    setDisplayCurrency(c);
    localStorage.setItem('displayCurrency', c);
  };

  // Save language preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('siteLanguage', language);
  }, [language]);

  useEffect(() => {
    // Safety timeout - ensure loading is false after 15 seconds max
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.debug('[APP] Safety timeout triggered - forcing loading to false');
        setLoading(false);
      }
    }, 15000);

    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    } else {
      checkAuth();
    }

    return () => clearTimeout(safetyTimeout);
  }, []);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const processSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/auth/session`, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });

      // Set session token as cookie (backup in case server cookie doesn't work)
      const isSecure = window.location.protocol === 'https:';
      const cookieString = isSecure
        ? `session_token=${response.data.session_token}; path=/; max-age=${60*60*24*7}; SameSite=None; Secure`
        : `session_token=${response.data.session_token}; path=/; max-age=${60*60*24*7}; SameSite=Lax`;
      document.cookie = cookieString;

      // Store token in localStorage
      localStorage.setItem('session_token', response.data.session_token);

      // Clean URL before fetching enriched user (so redirect is clean)
      window.history.replaceState({}, document.title, window.location.pathname);

      // Fetch enriched user from /auth/me so role is correctly computed from OWNER_EMAILS
      try {
        const meResponse = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${response.data.session_token}` },
          timeout: 10000
        });
        localStorage.setItem('user', JSON.stringify(meResponse.data));
        setUser(meResponse.data);
      } catch {
        // Fallback to OAuth user data if /auth/me fails
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Session processing error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    try {
      // First try to get session from localStorage (for page refresh persistence)
      const storedToken = localStorage.getItem('session_token');
      
      // If no stored token and no cookie, skip auth check
      if (!storedToken && !document.cookie.includes('session_token')) {
        console.debug('[AUTH] No session found, skipping auth check');
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Configure axios to send cookies with timeout
      const config = { 
        withCredentials: true,
        timeout: 10000  // 10 second timeout
      };
      
      // If we have a stored token, also send it as Authorization header as backup
      if (storedToken) {
        config.headers = { 'Authorization': `Bearer ${storedToken}` };
      }
      
      const response = await axios.get(`${API}/auth/me`, config);
      console.debug('[AUTH] User authenticated:', response.data.role);
      
      // Update localStorage with fresh data
      localStorage.setItem('user', JSON.stringify(response.data));
      
      setUser(response.data);
    } catch (error) {
      console.debug('[AUTH] Auth check skipped');
      
      // Clear stored data on auth failure
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state regardless of API response
      setUser(null);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const fetchWishlistIds = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/ids`, { withCredentials: true });
      setWishlistIds(response.data);
    } catch (error) {
      console.debug('Wishlist fetch skipped');
    }
  };

  const toggleWishlist = async (productId) => {
    try {
      const response = await axios.post(`${API}/wishlist/toggle?product_id=${productId}`, {}, { withCredentials: true });
      await fetchWishlistIds();
      return response.data.action;
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchCart();
    fetchWishlistIds();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <AppContext.Provider value={{ user, setUser, logout, cart, setCart, fetchCart, wishlistIds, toggleWishlist, fetchWishlistIds, language, setLanguage, displayCurrency, setDisplayCurrency: handleSetDisplayCurrency, convertPrice, currencySymbols, currencyRates, cartDrawerOpen, openCartDrawer, closeCartDrawer, openAuthModal, closeAuthModal }}>
        <BrowserRouter>
          <div className="App">
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-[#C8A951] focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold">
              Skip to content
            </a>
            <AnalyticsTracker />
            <PromoBanner />
            <main id="main-content">
            <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin h-8 w-8 border-4 border-[#C8A951] border-t-transparent rounded-full"></div></div>}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/product/:slug/:id" element={<ProductDetailPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/checkout/express/:draftId" element={<ExpressCheckoutPage />} />
            <Route path="/checkout/crypto" element={<CryptoPaymentPage />} />
            <Route path="/checkout/wise" element={<WisePaymentPage />} />
            <Route path="/checkout/revolut" element={<RevolutPaymentPage />} />
            <Route path="/checkout/eft" element={<EFTPaymentPage />} />
            <Route path="/checkout/paypal" element={<PayPalPaymentPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/buyer/orders" element={<BuyerOrdersPage />} />
            <Route path="/buyer/orders/:orderId" element={<BuyerOrderDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/return-policy" element={<ReturnPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </main>
          <CartDrawer isOpen={cartDrawerOpen} onClose={closeCartDrawer} />
          <AuthModal isOpen={authModalOpen} onClose={closeAuthModal} initialTab={authModalTab} />
          <WhatsAppButton />
          <BackToTop />
          <CookieConsent />
          <ExitIntentPopup />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AppContext.Provider>
    </HelmetProvider>
  );
}

export default App;